import React, { useState, useRef } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Custom icons (replacing lucide-react)
const GripVerticalIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
  </svg>
);

const XIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ResizeIcon = ({ direction = 'expand' }) => {
  if (direction === 'expand') {
    // Expand icon (for small blocks that can grow)
    return (
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 16 16">
        <path d="M16 15.5a.5.5 0 0 1-.5.5h-5a.5.5 0 0 1 0-1h3.793L6.146 6.854a.5.5 0 1 1 .708-.708L15 14.293V10.5a.5.5 0 0 1 1 0v5z" />
      </svg>
    );
  } else {
    // Shrink icon (for medium blocks that can shrink)
    return (
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 16 16">
        <path d="M5.5 0a.5.5 0 0 1 .5.5v4.793l2.146-2.147a.5.5 0 0 1 .708.708L6.207 6.5H11a.5.5 0 0 1 0 1H6.207l2.647 2.646a.5.5 0 0 1-.708.708L6 8.707V13.5a.5.5 0 0 1-1 0V8.707L2.854 10.854a.5.5 0 1 1-.708-.708L4.793 7.5H0a.5.5 0 0 1 0-1h4.793L2.146 4.854a.5.5 0 1 1 .708-.708L5 6.293V.5A.5.5 0 0 1 5.5 0z" />
      </svg>
    );
  }
};

const Block = ({ 
  id, 
  children, 
  size = 'medium', 
  title, 
  onRemove, 
  onResize,
  resizable = true,
  className = '',
  isDragging = false
}) => {
  const [currentSize, setCurrentSize] = useState(size);
  const [isResizing, setIsResizing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const blockRef = useRef(null);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: sortableIsDragging,
  } = useSortable({
    id,
    disabled: isResizing
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: sortableIsDragging ? 0.6 : 1,
  };

  const getSizeClasses = (blockSize) => {
    switch (blockSize) {
      case 'small':
        return 'col-span-2 row-span-1 min-h-[240px]';
      case 'medium':
        return 'col-span-3 row-span-2 min-h-[360px]';
      default:
        return 'col-span-3 row-span-2 min-h-[360px]';
    }
  };

  const handleResize = (newSize) => {
    setCurrentSize(newSize);
    onResize?.(id, newSize);
  };

  const handleRemove = () => {
    onRemove?.(id);
  };

  // Handle corner resize drag
  const handleResizeMouseDown = (e, direction) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);

    const startX = e.clientX;
    const startY = e.clientY;
    const startSize = currentSize;

    const handleMouseMove = (e) => {
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;
      const delta = Math.max(deltaX, deltaY);

      let newSize = startSize;
      if (delta > 50) {
        if (startSize === 'small') newSize = 'medium';
      } else if (delta < -50) {
        if (startSize === 'medium') newSize = 'small';
      }

      if (newSize !== currentSize) {
        handleResize(newSize);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <div
      ref={(node) => {
        setNodeRef(node);
        blockRef.current = node;
      }}
      style={style}
      className={`
        ${getSizeClasses(currentSize)}
        bg-white dark:bg-gray-800 rounded-3xl border border-gray-100/60 dark:border-gray-700/60 shadow-[0_2px_20px_-4px_rgba(0,0,0,0.06)]
        hover:shadow-[0_8px_40px_-8px_rgba(0,0,0,0.12)] hover:border-gray-200/80 dark:hover:border-gray-600/80
        transition-all duration-300 ease-out relative group overflow-hidden
        backdrop-blur-sm
        ${sortableIsDragging ? 'z-50 shadow-[0_20px_60px_-12px_rgba(0,0,0,0.25)]' : ''}
        ${isResizing ? 'z-40' : ''}
        ${className}
      `}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Background gradient overlay on hover */}
      <div className={`
        absolute inset-0 bg-gradient-to-br from-celadon-50/30 via-white/10 to-transparent 
        dark:from-celadon-900/20 dark:via-gray-800/10 dark:to-transparent
        opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none
        rounded-3xl
      `} />

      {/* Header with drag handle and controls */}
      <div className={`
        absolute top-4 right-4 flex items-center gap-2 
        opacity-0 group-hover:opacity-100 transition-all duration-200 z-10
        ${isHovered ? 'opacity-100' : ''}
      `}>
        {/* Drag handle */}
        <button
          {...attributes}
          {...listeners}
          className="p-2.5 rounded-2xl bg-white/90 dark:bg-gray-700/90 backdrop-blur-sm shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] 
                     hover:bg-white dark:hover:bg-gray-700 hover:shadow-[0_8px_30px_-6px_rgba(0,0,0,0.15)] 
                     text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 cursor-grab active:cursor-grabbing 
                     transition-all duration-200 transform hover:scale-105 border border-gray-100/50 dark:border-gray-600/50"
          title="Drag to reorder"
        >
          <GripVerticalIcon />
        </button>
        
        {/* Remove button */}
        <button
          onClick={handleRemove}
          className="p-2.5 rounded-2xl bg-white/90 dark:bg-gray-700/90 backdrop-blur-sm shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] 
                     hover:bg-gray-50 dark:hover:bg-gray-600 hover:shadow-[0_8px_30px_-6px_rgba(0,0,0,0.15)]
                     text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-all duration-200 transform hover:scale-105
                     border border-gray-100/50 dark:border-gray-600/50 hover:border-gray-200 dark:hover:border-gray-500"
          title="Remove block"
        >
          <XIcon />
        </button>
      </div>

      {/* Resize handles */}
      {resizable && (
        <>
          {/* Bottom-right corner resize handle */}
          <div
                className={`
                  absolute bottom-3 right-3 w-6 h-6 
                  opacity-0 group-hover:opacity-100 transition-all duration-200 z-10
                  cursor-se-resize rounded-xl bg-white/90 dark:bg-gray-700/90 backdrop-blur-sm 
                  shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:bg-white dark:hover:bg-gray-700
                  hover:shadow-[0_8px_30px_-6px_rgba(0,0,0,0.15)] 
                  flex items-center justify-center border border-gray-100/50 dark:border-gray-600/50
                  ${isHovered ? 'opacity-100' : ''}
                `}
                onMouseDown={(e) => handleResizeMouseDown(e, 'se')}
                title="Drag to resize"
              >
                <ResizeIcon direction={currentSize === 'small' ? 'expand' : 'expand'} />
              </div>
        </>
      )}

      {/* Block content */}
      <div className="h-full p-6 relative z-[1]">
        {title && (
          <div className="flex items-center justify-between mb-4 pr-20">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 tracking-tight">
              {title}
            </h3>
          </div>
        )}
        <div className="h-full overflow-hidden">
          {children}
        </div>
      </div>

      {/* Loading overlay when dragging */}
      {sortableIsDragging && (
        <div className="absolute inset-0 bg-white/60 dark:bg-gray-800/60 backdrop-blur-md rounded-3xl flex items-center justify-center z-20 border border-gray-200/50 dark:border-gray-600/50">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-celadon-200 border-t-celadon-500 rounded-full animate-spin mx-auto mb-2"></div>
            <p className="text-sm text-gray-700 dark:text-gray-200 font-medium">Moving...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Block; 